import { JobEvent, JobStatus, ListedJob } from "@common/api/v1.1/types/jobs";
import { JobState, State, LoggedInState } from "../types";

export interface UpdateJobsAction {
  blockingStep: string;
  eventType: JobEvent | null;
  jobs: Array<ListedJob>;
  type: "UPDATE JOBS";
}

export const updateJobs = (state: State, action: UpdateJobsAction): State => {
  let activeJobId = state.activeJobId;
  if (action.jobs.length < 1) {
    return state;
  }

  // No event means it's the actual job list update from pagination or page load
  if (action.eventType === null) {
    return {
      ...state,
      jobs: action.jobs.reduce((prev: State["jobs"], j: ListedJob): State["jobs"] => {
        prev[j.id] = {
          data: j,
          override: undefined,
        };
        return prev;
      }, {}),
    };
  } 
  let jobs = state.jobs;

  // we don't need to update the jobs that aren't shown since
  // they will be refetched (with their updated status) when the
  // user paginates.  
  let actionedJobsCurrentlyShown = action.jobs.filter(x => !!jobs[x.id]);
  
  switch (action.eventType) {
    case JobEvent.JobBlocked:
    case JobEvent.JobCheckedOut:
    case JobEvent.JobCollaborationChanged:
    case JobEvent.JobCreated:
    case JobEvent.JobProgressChanged:
    case JobEvent.JobStatusChanged:
      jobs = actionedJobsCurrentlyShown.reduce((prev: State["jobs"], j: ListedJob): State["jobs"] => {
        if (j.status === JobStatus.BLOCKED && j.blocking_step === action.blockingStep) {
          const newJobData = {
            data: j,
            override: state.jobs[j.id]?.override,
          };
          if (prev === state.jobs) {
            return {
              ...state.jobs,
              [j.id]: newJobData,
            };
          }
          prev[j.id] = newJobData;
        } else if (j.id in prev) {
          let toReturn = prev;
          if (prev === state.jobs) {
            toReturn = { ...prev };
          }
          delete toReturn[j.id];
          return toReturn;
        }
        return prev;
      }, state.jobs);
      break;
    case JobEvent.JobCheckedIn:
    case JobEvent.JobCancelled:
    case JobEvent.JobDeleted:
    case JobEvent.JobFailed:
      jobs = actionedJobsCurrentlyShown.reduce((prev: State["jobs"], j: ListedJob): State["jobs"] => {
        if (!(j.id in prev)) {
          return prev;
        }
        if (prev === state.jobs) {
          const toReturn = { ...prev };
          delete toReturn[j.id];
          return toReturn;
        }
        delete prev[j.id];
        return prev;
      }, state.jobs);
      break;
  }

  let job = state.job;

  let jobId : string = "";
  if (job.state === JobState.ready) {
    jobId = job.job.id;
  } else if (job.state === JobState.loading) {
    jobId = job.jobId;
  }

  // We received an event on the job that's currently loaded/loading
  // Setting the job state to initial will cause a reload
  // of the job (or an unload if it's gone).  
  let actionedJob = action.jobs.find(x => x.id === jobId);
  if (jobId && !!actionedJob) {
    let auth = <LoggedInState> state.auth;

    // Somebody else checked it out
    if (!!actionedJob && actionedJob.claimed_by?.id !== auth.user.id ) {
      job = { state: JobState.initial };
      activeJobId = undefined;
    }

    // TODO : There could be other events that would require a reload
    if (action.eventType in [
      JobEvent.JobCancelled, 
      JobEvent.JobFailed,
      JobEvent.JobDeleted,
      JobEvent.JobContentChanged]) {
        job = { state: JobState.initial };
    }
  }

  return {
    ...state,
    job,
    jobs,
    activeJobId
  };
};
